/** Opt-in, memory-only diagnostics. Never accepts a URL, token, or raw error. */
export class PlaybackTrace {
  private rows: object[] = [];
  private started = Date.now();
  private lastSample = 0;
  private video: HTMLVideoElement;
  private listeners: Array<[string, EventListener]> = [];

  constructor(private context: () => {
    engine: 'hls' | 'native' | 'none'; stream: 'pmu' | 'meeting' | 'legacy';
    maxHeight: number; level: number; wanted: boolean;
  }) {}

  attach(video: HTMLVideoElement): void {
    if (this.video === video) { return; }
    this.detach();
    this.video = video;
    ['play', 'playing', 'pause', 'waiting', 'stalled', 'ended', 'error', 'emptied',
      'loadedmetadata', 'canplay', 'seeking', 'seeked', 'timeupdate'].forEach((name) => {
      const listener = () => {
        if (name === 'timeupdate') {
          if (Date.now() - this.lastSample < 5000) { return; }
          this.lastSample = Date.now();
        }
        this.record(name);
      };
      video.addEventListener(name, listener);
      this.listeners.push([name, listener]);
    });
  }

  record(event: string, status = 0): void {
    // Browser media getters can throw during detach; diagnostics must never
    // interrupt the recovery path they are observing.
    try { this.capture(event, status); } catch (_) { /* Best-effort observation only. */ }
  }

  private capture(event: string, status: number): void {
    // Only fixed machine codes belong here; reject arbitrary strings entirely.
    if (!/^[a-zA-Z0-9_]{1,64}$/.test(event)) { return; }
    const video = this.video;
    const numeric = (value: number) => Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
    const ranges = (value: TimeRanges) => {
      try {
        return value && value.length ? [numeric(value.start(0)), numeric(value.end(value.length - 1))] : [];
      } catch (_) { return []; }
    };
    let ahead = 0;
    const buffered = video && video.buffered;
    if (video) {
      for (let i = 0; i < buffered.length; i++) {
        if (buffered.start(i) <= video.currentTime && buffered.end(i) > video.currentTime) {
          ahead = buffered.end(i) - video.currentTime; break;
        }
      }
    }
    this.rows.push({
      ms: Date.now() - this.started, event, ...this.context(),
      status: Number.isInteger(status) && status >= 0 && status <= 599 ? status : 0,
      visible: typeof document !== 'undefined' && document.visibilityState !== 'hidden',
      online: typeof navigator === 'undefined' || navigator.onLine !== false,
      ...(video ? {
        time: numeric(video.currentTime), ahead: numeric(ahead), buffered: ranges(buffered),
        seekable: ranges(video.seekable), paused: video.paused, ended: video.ended, seeking: video.seeking,
        ready: video.readyState, network: video.networkState, error: video.error ? video.error.code : 0,
        width: video.videoWidth, height: video.videoHeight,
      } : {}),
    });
    if (this.rows.length > 120) { this.rows.shift(); }
  }

  report(): string {
    this.record('report');
    return JSON.stringify({ version: 'iphone-recovery-1', startedAt: new Date(this.started).toISOString(), events: this.rows });
  }

  detach(): void {
    if (this.video) { this.listeners.forEach(([name, listener]) => this.video.removeEventListener(name, listener)); }
    this.listeners = [];
    this.video = undefined;
  }
}
